import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { toast } from "sonner";

/* Site-wide translation — Google Translate page engine (free, no key).
   Sets the googtrans cookie and loads the translate element once; every page
   (including dynamic content) is translated. Choice persists across pages.

   On first visit, we peek at navigator.language and preselect a supported
   language automatically so overseas users don't have to click the globe.
   The autodetect only fires once per device and never overrides an explicit
   user choice (tracked via localStorage). */


const LANGS = [
  // English + European
  ["en", "English"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"],
  ["pt", "Português"], ["it", "Italiano"], ["nl", "Nederlands"], ["pl", "Polski"],
  ["ru", "Русский"], ["uk", "Українська"], ["cs", "Čeština"], ["ro", "Română"],
  ["el", "Ελληνικά"], ["sv", "Svenska"], ["da", "Dansk"], ["no", "Norsk"],
  ["fi", "Suomi"], ["hu", "Magyar"],
  // Indian subcontinent
  ["hi", "हिन्दी"], ["ml", "മലയാളം"], ["ta", "தமிழ்"], ["te", "తెలుగు"],
  ["kn", "ಕನ್ನಡ"], ["mr", "मराठी"], ["gu", "ગુજરાતી"], ["bn", "বাংলা"],
  ["pa", "ਪੰਜਾਬੀ"], ["ur", "اردو"], ["si", "සිංහල"], ["ne", "नेपाली"],
  // East + Southeast Asia
  ["zh-CN", "中文 (简体)"], ["zh-TW", "中文 (繁體)"], ["ja", "日本語"], ["ko", "한국어"],
  ["th", "ไทย"], ["vi", "Tiếng Việt"], ["id", "Bahasa Indonesia"], ["ms", "Bahasa Melayu"],
  ["fil", "Filipino"],
  // Middle East + Africa
  ["ar", "العربية"], ["he", "עברית"], ["fa", "فارسی"], ["tr", "Türkçe"],
  ["sw", "Kiswahili"], ["af", "Afrikaans"], ["am", "አማርኛ"],
];

const AUTODETECT_KEY = "tp_lang_autodetected";

// Map browser locale (e.g. "pt-BR", "zh-Hant-HK") to the closest supported
// Google Translate code. Unknown locales fall back to English (no cookie set).
function detectFromBrowser() {
  if (typeof navigator === "undefined") return "en";
  const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  const supported = new Set(LANGS.map(([c]) => c.toLowerCase()));
  // Chinese variants
  if (raw.startsWith("zh")) {
    if (raw.includes("tw") || raw.includes("hk") || raw.includes("hant")) return "zh-TW";
    return "zh-CN";
  }
  // Norwegian: browsers send nb / nn — we only support "no"
  if (raw.startsWith("nb") || raw.startsWith("nn")) return "no";
  // Filipino/Tagalog
  if (raw.startsWith("tl") || raw.startsWith("fil")) return "fil";
  // Exact match first (with region), then base language
  if (supported.has(raw)) return raw;
  const base = raw.split("-")[0];
  if (supported.has(base)) return base;
  return "en";
}

const getLang = () => {
  const m = document.cookie.match(/googtrans=\/en\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "en";
};

const setCookie = (lang) => {
  const host = window.location.hostname;
  const domains = ["", `; domain=${host}`, `; domain=.${host.split(".").slice(-2).join(".")}`];
  domains.forEach((d) => {
    if (lang === "en") {
      document.cookie = `googtrans=; path=/${d}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } else {
      document.cookie = `googtrans=/en/${lang}; path=/${d}`;
    }
  });
};

function ensureScript() {
  if (document.getElementById("gt-script")) return;
  window.googleTranslateElementInit = () => {
    /* eslint-disable no-new */
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false }, "google_translate_element");
  };
  const s = document.createElement("script");
  s.id = "gt-script";
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  document.body.appendChild(s);
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(getLang);
  const ref = useRef(null);

  useEffect(() => {
    // Autodetect from navigator.language once per device, only if the user
    // hasn't already made a choice (empty googtrans cookie).
    try {
      const already = localStorage.getItem(AUTODETECT_KEY);
      const currentLang = getLang();
      if (!already && currentLang === "en") {
        const detected = detectFromBrowser();
        if (detected !== "en") {
          setCookie(detected);
          localStorage.setItem(AUTODETECT_KEY, detected);
          // Flag that we owe the user an "undo" toast on the next load
          sessionStorage.setItem("tp_lang_autodetect_toast", detected);
          window.location.reload();
          return;
        }
        localStorage.setItem(AUTODETECT_KEY, "en");
      }
    } catch { /* privacy-mode or storage blocked */ }

    // Show the autodetect toast once, after the reload
    try {
      const pending = sessionStorage.getItem("tp_lang_autodetect_toast");
      if (pending) {
        sessionStorage.removeItem("tp_lang_autodetect_toast");
        const label = (LANGS.find(([c]) => c === pending) || [])[1] || pending;
        toast(`Reading in ${label}`, {
          description: "We detected your browser language. Switch back to English?",
          duration: 6000,
          action: {
            label: "Use English",
            onClick: () => {
              setCookie("en");
              try { localStorage.setItem(AUTODETECT_KEY, "en"); } catch { /* ignore */ }
              window.location.reload();
            },
          },
        });
      }
    } catch { /* ignore */ }

    if (getLang() !== "en") ensureScript();
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (code) => {
    setCookie(code);
    // Mark as user-chosen so future autodetects don't override it
    try { localStorage.setItem(AUTODETECT_KEY, code); } catch { /* ignore */ }
    setLang(code);
    setOpen(false);
    window.location.reload(); // translate engine re-applies from cookie on load
  };

  const current = LANGS.find(([c]) => c === lang) || LANGS[0];

  return (
    <div className="relative notranslate" ref={ref} data-testid="language-switcher">
      <div id="google_translate_element" className="hidden" />
      <button onClick={() => setOpen(!open)} data-testid="language-toggle" title="Translate this site"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-600 text-zinc-600 hover:text-zinc-900 border border-transparent hover:border-zinc-300">
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{current[0] === "en" ? "EN" : current[0].toUpperCase().slice(0, 2)}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-52 max-h-80 overflow-y-auto bg-white border border-zinc-300 shadow-lg z-[70]" data-testid="language-menu">
          {LANGS.map(([code, label]) => (
            <button key={code} onClick={() => pick(code)} data-testid={`lang-${code}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between ${code === lang ? "font-700 text-[#4285F4]" : "text-zinc-700"}`}>
              {label} {code === lang && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-zinc-100">
            Machine translation by Google — verify important details in English
          </div>
        </div>
      )}
    </div>
  );
}
